// El remito impreso: el documento que viaja con el reparto.
// Vive aca porque lo abren varias pantallas (Produccion y Pedidos web) y tiene
// que salir IDENTICO en todas. Se imprimen siempre dos vias: Via 1 (cliente) y
// Via 2 (control).

import { api } from "./api";
import { formatearPesos } from "../lib/money";
import { iconoImpresora } from "./iconos";
import { nombreTurno, type Turno } from "../lib/types";

export interface PedidoDetalleResp {
  pedido: {
    id: number;
    fecha_entrega: string;
    turno: Turno;
    total: number;
    descuento: number;
    cliente_nombre: string;
    cliente_codigo: string;
    cliente_direccion: string | null;
    empresa_nombre: string | null;
    empresa_direccion: string | null;
    empresa_ciudad: string | null;
    empresa_telefono: string | null;
  };
  lineas: Array<{
    producto_id: number;
    cantidad: number;
    precio_unitario: number;
    subtotal: number;
    codigo: number;
    descripcion: string;
    empaque: number;
  }>;
}

function esc(s: unknown): string {
  return String(s ?? "").replace(/[&<>"]/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[c] as string,
  );
}

/**
 * La marca de la casa, en TEXTO: `rb` y al lado `roberto barcos`.
 *
 * A proposito no es una imagen: asi se imprime nitida en cualquier impresora
 * (incluida la termica), no depende de ningun archivo y no hay que mantener
 * versiones en distintos tamanos. La usan el remito, la produccion, las
 * cuentas y la lista de precios, para que salga identica en todos los papeles.
 */
export function marcaEmpresa(alto: "chico" | "grande" = "chico"): string {
  const rb = alto === "grande" ? "text-4xl" : "text-3xl";
  const nombre = alto === "grande" ? "text-lg" : "text-base";
  return `
    <span class="inline-flex items-baseline gap-1.5 leading-none">
      <span class="${rb} font-black tracking-tighter text-black">rb</span>
      <span class="${nombre} font-light lowercase tracking-wide text-black">roberto barcos</span>
    </span>`;
}

/**
 * Imprime SOLO el html que se le pasa, en un documento aparte.
 *
 * Antes esto metia el html en un #print-target, le ponia una clase al body y
 * dejaba que el CSS escondiera el resto de la pantalla, sacando la clase en el
 * evento `afterprint`. En la computadora funcionaba; en el celular imprimia
 * TODA la pagina —el resumen y los dos turnos— porque en Android `print()` no
 * abre un dialogo que bloquee: `afterprint` dispara enseguida y la clase se iba
 * antes de que se armara la hoja.
 *
 * Con un iframe no hay nada que esconder ni ninguna clase que llegue tarde: lo
 * que se manda a la impresora es un documento que solo contiene esto. Se le
 * copian las hojas de estilo de la pagina para que salga con el mismo formato.
 */
export function imprimirElemento(html: string): void {
  const estilos = Array.from(
    document.querySelectorAll('link[rel="stylesheet"], style'),
  )
    .map((n) => n.outerHTML)
    .join("");

  const marco = document.createElement("iframe");
  marco.setAttribute("aria-hidden", "true");
  // Fuera de la vista pero CON tamano: un iframe de 0x0 no siempre se maqueta.
  marco.style.cssText =
    "position:fixed; left:-10000px; top:0; width:820px; height:1200px; border:0;";
  marco.srcdoc = `<!doctype html><html lang="es"><head><meta charset="utf-8">${estilos}</head><body>${html}</body></html>`;

  marco.addEventListener("load", () => {
    const v = marco.contentWindow;
    if (!v) {
      marco.remove();
      return;
    }
    v.focus();
    v.print();
    // print() vuelve enseguida en varios navegadores, asi que el iframe se saca
    // por tiempo y no por `afterprint`: si se sacara antes de que la impresora
    // lea el documento, saldria una hoja en blanco.
    window.setTimeout(() => marco.remove(), 60000);
  });

  document.body.appendChild(marco);
}

/** Una via del remito. `via` 1 = cliente, 2 = control. */
export function remitoVia(
  p: PedidoDetalleResp["pedido"],
  lineas: PedidoDetalleResp["lineas"],
  via: number,
): string {
  const filas = lineas
    .map(
      (l) => `<tr class="border-t border-black/20">
        <td class="py-1 pr-2">${esc(l.descripcion)}</td>
        <td class="py-1 pr-2 text-right">${l.cantidad}</td>
        <td class="py-1 pr-2 text-right">${formatearPesos(l.precio_unitario)}</td>
        <td class="py-1 text-right">${formatearPesos(l.subtotal)}</td>
      </tr>`,
    )
    .join("");
  // La ciudad tambien sale: "Jose Nasazzi 1022 · Montevideo, Uruguay · Tel ...".
  const emite = p.empresa_nombre
    ? [
        esc(p.empresa_nombre),
        p.empresa_direccion ? esc(p.empresa_direccion) : "",
        p.empresa_ciudad ? esc(p.empresa_ciudad) : "",
        p.empresa_telefono ? `Tel. ${esc(p.empresa_telefono)}` : "",
      ]
        .filter(Boolean)
        .join(" · ")
    : "";
  return `
    <div class="mx-auto max-w-lg bg-white p-4 text-black ${via === 1 ? "print:break-after-page" : ""}">
      <div class="flex items-start justify-between">
        <div class="mb-2">
          ${marcaEmpresa()}
          <h2 class="mt-1 text-xl font-bold">Remito</h2>
        </div>
        <span class="rounded border border-black/30 px-2 py-0.5 text-xs font-bold">VIA ${via} — ${via === 1 ? "CLIENTE" : "CONTROL"}</span>
      </div>
      ${emite ? `<div class="border-b border-black/20 pb-1 text-xs"><b>Emite:</b> ${emite}</div>` : ""}
      <div class="mt-1 text-sm">
        <div><b>Cliente:</b> ${esc(p.cliente_codigo)} — ${esc(p.cliente_nombre)}</div>
        ${p.cliente_direccion ? `<div><b>Direccion:</b> ${esc(p.cliente_direccion)}</div>` : ""}
        <div><b>Entrega:</b> ${esc(p.fecha_entrega)}${p.turno ? ` — ${esc(nombreTurno(p.turno))}` : ""}</div>
        <div><b>Remito:</b> #${p.id}</div>
      </div>
      <table class="mt-3 w-full text-sm">
        <thead><tr class="text-left text-xs">
          <th>Producto</th><th class="text-right">Cant.</th><th class="text-right">P.Unit</th><th class="text-right">Subtotal</th>
        </tr></thead>
        <tbody>${filas}</tbody>
      </table>
      ${p.descuento ? `<div class="mt-2 text-right text-sm">Descuento: ${formatearPesos(p.descuento)}</div>` : ""}
      <div class="mt-3 text-right text-lg font-bold">Total: ${formatearPesos(p.total)}</div>
      <div class="mt-6 flex justify-between text-xs text-black/60">
        <span>Firma: ____________________</span>
        <span>Aclaracion: ____________________</span>
      </div>
    </div>`;
}

/** Abre el remito de un pedido en un <dialog>, con boton de impresion. */
export async function abrirRemito(dlg: HTMLDialogElement, id: number): Promise<void> {
  dlg.innerHTML = `<div class="p-6 text-masa-400">Cargando remito...</div>`;
  dlg.showModal();
  try {
    const r = await api.get<PedidoDetalleResp>(`/api/pedidos/${id}`);
    const via1 = remitoVia(r.pedido, r.lineas, 1);
    const via2 = remitoVia(r.pedido, r.lineas, 2);
    const paraImprimir = via1 + via2;
    dlg.innerHTML = `
      <div class="w-[34rem] max-w-full">
        ${via1}
        <div class="my-3 border-t-2 border-dashed border-crema-300"></div>
        ${via2}
        <div class="no-print flex justify-end gap-2 border-t border-crema-200 p-3">
          <button id="p_cerrar" class="btn-sec">Cerrar</button>
          <button id="p_imprimir" class="btn-primario">${iconoImpresora} Imprimir remito (2 vias)</button>
        </div>
      </div>`;
    dlg.querySelector("#p_cerrar")?.addEventListener("click", () => dlg.close());
    dlg.querySelector("#p_imprimir")?.addEventListener("click", () => imprimirElemento(paraImprimir));
  } catch (e) {
    dlg.innerHTML = `<div class="p-6"><p class="text-red-700">${esc(
      e instanceof Error ? e.message : "Error",
    )}</p><div class="mt-3 text-right"><button id="p_cerrar" class="btn-sec">Cerrar</button></div></div>`;
    dlg.querySelector("#p_cerrar")?.addEventListener("click", () => dlg.close());
  }
}
